import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Bell, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { api } from "../lib/api";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";

const PORTAL_NAV = [
  { to: "/cliente/home", label: "Início" },
  { to: "/cliente/chamados", label: "Chamados de Troca" },
  { to: "/cliente/servico", label: "Chamados de Serviço" },
  { to: "/portal/financeiro", label: "Financeiro" },
  { to: "/portal/contratos", label: "Contratos" },
  { to: "/cliente/meu_perfil", label: "Meu perfil" },
];

export const PortalShell = ({ children }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [empresaNome, setEmpresaNome] = useState("");
  const [picture, setPicture] = useState(null);
  const [notifs, setNotifs] = useState({ notificacoes: [], nao_lidas: 0 });
  const [showNotifs, setShowNotifs] = useState(false);
  const [showMobile, setShowMobile] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/portal/me").then((r) => {
      setEmpresaNome(r.data.cliente?.nome || user.name);
      setPicture(r.data.user?.picture);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/portal/notificacoes").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user]);

  if (!user) return null;

  const initials = (empresaNome || user.name || "?")
    .split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const marcarLida = async (chave, link) => {
    try {
      await api.post(`/portal/notificacoes/${encodeURIComponent(chave)}/lida`);
      setNotifs((n) => ({
        ...n,
        nao_lidas: Math.max(0, n.nao_lidas - 1),
        notificacoes: n.notificacoes.map((x) => x.chave === chave ? { ...x, lida: true } : x),
      }));
    } catch {}
    setShowNotifs(false);
    if (link) nav(link);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* TOPBAR fixa */}
      <header
        className="sticky top-0 z-40 text-white shadow-sm"
        style={{ background: "#1B3A6B" }}
        data-testid="portal-topbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5 rounded hover:bg-white/10"
              onClick={() => setShowMobile((s) => !s)}
              data-testid="portal-mobile-toggle"
              aria-label="Menu"
            >
              {showMobile ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/cliente/home" data-testid="portal-logo-link">
              <Logo size="sm" variant="dark" />
            </Link>
          </div>

          {/* nav desktop */}
          <nav className="hidden lg:flex items-center gap-1">
            {PORTAL_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md transition ${
                    isActive
                      ? "bg-white/15 text-white font-semibold"
                      : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* sino notificações */}
            <div className="relative">
              <button
                onClick={() => setShowNotifs((s) => !s)}
                className="relative p-2 rounded-full hover:bg-white/10"
                data-testid="portal-notif-bell"
                aria-label="Notificações"
              >
                <Bell size={18} />
                {notifs.nao_lidas > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 text-[10px] font-bold text-white rounded-full px-1 min-w-[16px] text-center"
                    style={{ background: "#F97316" }}
                    data-testid="portal-notif-badge"
                  >
                    {notifs.nao_lidas}
                  </span>
                )}
              </button>
              {showNotifs && (
                <div
                  className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border overflow-hidden text-gray-900 max-h-[70vh] overflow-y-auto"
                  data-testid="portal-notif-dropdown"
                >
                  <div className="px-4 py-3 border-b" style={{ background: "#F8FAFC" }}>
                    <div className="font-semibold text-sm" style={{ color: "#1B3A6B" }}>
                      Notificações
                    </div>
                    <div className="text-xs text-gray-500">
                      {notifs.nao_lidas} não lida(s)
                    </div>
                  </div>
                  {notifs.notificacoes.length === 0 && (
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      Sem notificações no momento
                    </div>
                  )}
                  {notifs.notificacoes.map((n) => (
                    <button
                      key={n.chave}
                      onClick={() => marcarLida(n.chave, n.link)}
                      data-testid={`portal-notif-item-${n.chave}`}
                      className={`w-full text-left px-4 py-3 border-b hover:bg-orange-50 transition ${
                        n.lida ? "opacity-60" : ""
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.lida && (
                          <span
                            className="mt-1.5 w-2 h-2 rounded-full shrink-0"
                            style={{ background: "#F97316" }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold" style={{ color: "#1B3A6B" }}>
                            {n.titulo}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">{n.mensagem}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* avatar */}
            <div className="hidden sm:flex items-center gap-2">
              <Avatar className="h-9 w-9 border-2" style={{ borderColor: "#F97316" }}>
                {picture ? (
                  <img src={picture} alt={empresaNome} className="rounded-full" />
                ) : (
                  <AvatarFallback className="bg-white/10 text-white text-xs">
                    {initials}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="hidden md:block min-w-0 max-w-[180px]">
                <div className="text-sm font-semibold truncate" data-testid="portal-empresa-nome">
                  {empresaNome || user.name}
                </div>
                <div className="text-[10px] text-white/60 uppercase tracking-wider">
                  Cliente
                </div>
              </div>
            </div>
            <Button
              data-testid="portal-logout-button"
              variant="outline"
              size="sm"
              className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white"
              onClick={() => {
                logout();
                nav("/cliente/login");
              }}
            >
              <LogOut size={14} />
            </Button>
          </div>
        </div>

        {/* nav mobile */}
        {showMobile && (
          <nav className="lg:hidden border-t border-white/10 px-4 py-3 space-y-1" data-testid="portal-mobile-nav">
            {PORTAL_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setShowMobile(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm rounded ${
                    isActive ? "bg-white/15 font-semibold" : "text-white/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
};
